import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from "@reduxjs/toolkit"

import {
  AssignOrderProps,
  DataStatus,
  Order,
  OrderWithoutPayment,
  Payment,
  PaymentsProps,
  RootState,
  Team,
} from "@/types"
import {
  fetchAssignOrderToTransaction,
  fetchDeletePayment,
  fetchOrdersWithoutPayments,
  fetchPayments,
  fetchSetPaymentAsTip,
  fetchTogglePaymentStatus,
  fetchUnsettledPayments,
  sortPaymentsByDate,
} from "@/utils"
import { handleStandardError } from "@/utils/errors"

import { setCurrentTeam } from "./teams"
import { clearDataOnLogout } from "./user"

const paymentsAdapter = createEntityAdapter<Payment>()
const ordersWithoutPaymentsAdapter = createEntityAdapter<OrderWithoutPayment>()

type PaymentsState = EntityState<Payment> & {
  ordersWithoutPayments: EntityState<OrderWithoutPayment>
  ordersWithoutPaymentsStatus: DataStatus | null
  paymentsStatus: DataStatus | null
  unsettledPaymentsStatus: DataStatus | null
  updateTimestamp: string | null
}

const initialState: PaymentsState = paymentsAdapter.getInitialState({
  ordersWithoutPayments: ordersWithoutPaymentsAdapter.getInitialState(),
  ordersWithoutPaymentsStatus: null,
  paymentsStatus: null,
  unsettledPaymentsStatus: null,
  updateTimestamp: null,
})

export const getPayments = createAsyncThunk("payments/getPayments", async ({ teamId, params }: PaymentsProps) => {
  const { response, json } = await fetchPayments({ teamId, params })

  return {
    ok: response.ok,
    status: response.status,
    data: json.data,
  }
})

export const getUnsettledPayments = createAsyncThunk("payments/getUnsettledPayments", async (teamId: Team["id"]) => {
  const { response, json } = await fetchUnsettledPayments(teamId)

  return {
    ok: response.ok,
    status: response.status,
    data: json.data,
  }
})

export const getAllPayments = createAsyncThunk(
  "payments/getAllPayments",
  async ({ teamId, paymentsParams }: { teamId: Team["id"]; paymentsParams: PaymentsProps["params"] }, { dispatch }) => {
    dispatch(resetPayments())
    dispatch(getPayments({ teamId, params: paymentsParams }))
    dispatch(getUnsettledPayments(teamId))
    dispatch(getOrdersWithoutPayments(teamId))
  }
)

export const getOrdersWithoutPayments = createAsyncThunk(
  "payments/getOrdersWithoutPayments",
  async (teamId: Team["id"]) => {
    const { response, json } = await fetchOrdersWithoutPayments(teamId)

    return {
      ok: response.ok,
      status: response.status,
      data: json.data,
    }
  }
)

export const setPaymentAsTip = createAsyncThunk(
  "payments/setPaymentAsTip",
  async (paymentId: Payment["id"], { dispatch }) => {
    const { response, json } = await fetchSetPaymentAsTip(paymentId)

    const { data: payment } = json

    if (!response.ok || !payment) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(setUpdateTimestamp())

    return {
      ok: response.ok,
      status: response.status,
      data: json.data,
    }
  }
)

export const assignOrder = createAsyncThunk(
  "payments/assignOrder",
  async ({ transactionCode, body }: AssignOrderProps, { dispatch }) => {
    const { response, json } = await fetchAssignOrderToTransaction({ transactionCode, body })

    const { data: payment } = json

    if (!response.ok || !payment) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(setUpdateTimestamp())

    return {
      ok: response.ok,
      status: response.status,
      data: json.data,
    }
  }
)

export const deletePayment = createAsyncThunk(
  "payments/deletePayment",
  async (paymentId: Payment["id"], { dispatch }) => {
    const { response } = await fetchDeletePayment(paymentId)

    if (!response.ok) {
      handleStandardError(response)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(setUpdateTimestamp())

    return {
      ok: response.ok,
      status: response.status,
    }
  }
)

export const togglePaymentStatus = createAsyncThunk(
  "payments/togglePaymentStatus",
  async (orderId: Order["id"], { dispatch }) => {
    const { response } = await fetchTogglePaymentStatus(orderId)

    if (!response.ok) {
      handleStandardError(response)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(setUpdateTimestamp())

    return {
      ok: response.ok,
      status: response.status,
    }
  }
)

const paymentsSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    resetPayments(state) {
      state.paymentsStatus = null
      state.unsettledPaymentsStatus = null
      paymentsAdapter.removeAll(state)
    },

    setUpdateTimestamp(state) {
      state.updateTimestamp = new Date().toISOString()
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPayments.pending, (state) => {
      state.paymentsStatus = "loading"
    })

    builder.addCase(getPayments.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) paymentsAdapter.upsertMany(state, payload.data)

        state.paymentsStatus = "fetched"
      } else {
        state.paymentsStatus = "error"
      }
    })

    builder.addCase(getUnsettledPayments.pending, (state) => {
      state.unsettledPaymentsStatus = "loading"
    })

    builder.addCase(getUnsettledPayments.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) paymentsAdapter.upsertMany(state, payload.data)

        state.unsettledPaymentsStatus = "fetched"
      } else {
        state.unsettledPaymentsStatus = "error"
      }
    })

    builder.addCase(getOrdersWithoutPayments.pending, (state) => {
      state.ordersWithoutPaymentsStatus = "loading"
    })

    builder.addCase(getOrdersWithoutPayments.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) ordersWithoutPaymentsAdapter.setAll(state.ordersWithoutPayments, payload.data)

        state.ordersWithoutPaymentsStatus = "fetched"
      } else {
        state.ordersWithoutPaymentsStatus = "error"
      }
    })

    builder.addCase(setCurrentTeam, (state) => {
      paymentsAdapter.removeAll(state)
    })

    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

const {
  selectById: selectOrdersWithoutPaymentsByIdSelector,
  selectIds: selectOrdersWithoutPaymentsIdsSelector,
} = ordersWithoutPaymentsAdapter.getSelectors((state: RootState) => state.payments.ordersWithoutPayments)

export const selectOrdersWithoutPaymentsById = (id: OrderWithoutPayment["id"]) => (state: RootState) =>
  selectOrdersWithoutPaymentsByIdSelector(state, id)

export const selectOrdersWithoutPaymentsIds = selectOrdersWithoutPaymentsIdsSelector as (
  state: RootState
) => Array<OrderWithoutPayment["id"]>

const { selectById, selectAll } = paymentsAdapter.getSelectors((state: RootState) => state.payments)

export const selectPaymentById = (id: Payment["id"]) => (state: RootState) => selectById(state, id)

export const selectSettledPayments = createSelector(selectAll, (payments) =>
  payments.filter((payment) => payment.order_uid !== null).sort(sortPaymentsByDate)
)

export const selectSortedSettledPaymentsIds = (sorter: ((a: Payment, b: Payment) => number) | null) =>
  createSelector(selectSettledPayments, (payments) => {
    return sorter ? payments.sort(sorter).map((payment) => payment.id) : payments.map((payment) => payment.id)
  })

// export const selectFilteredSettledPayments = (...filters: Array<(payment: Payment) => boolean>) =>
//   createSelector(selectSettledPayments, (payments) =>
//     payments.filter((payment) => filters.every((filter) => filter(payment)))
//   )

// export const selectFilteredSettledPaymentsIds = (...filters: Array<(payment: Payment) => boolean>) =>
//   createSelector(selectFilteredSettledPayments(...filters), (payments) => payments.map((payment) => payment.id))

// export const selectFilteredSortedSettledPaymentsIds = (
//   sorter: ((a: Payment, b: Payment) => number) | null,
//   ...filters: Array<(payment: Payment) => boolean>
// ) =>
//   createSelector(selectFilteredSettledPayments(...filters), (payments) => {
//     return sorter ? payments.sort(sorter).map((payment) => payment.id) : payments.map((payment) => payment.id)
//   })

export const selectUnsettledPayments = createSelector(selectAll, (payments) =>
  payments.filter((payment) => payment.order_uid === null)
)

export const selectUnsettledPaymentsIds = createSelector(selectUnsettledPayments, (payments) =>
  payments.map((payment) => payment.id)
)

export const selectPaymentsStatus = (state: RootState) => state.payments.paymentsStatus
export const selectUnsettledPaymentsStatus = (state: RootState) => state.payments.unsettledPaymentsStatus
export const selectOrdersWithoutPaymentsStatus = (state: RootState) => state.payments.ordersWithoutPaymentsStatus
export const selectPaymentsUpdateTimestamp = (state: RootState) => state.payments.updateTimestamp

export const { resetPayments, setUpdateTimestamp } = paymentsSlice.actions

export const paymentsReducer = paymentsSlice.reducer
